import { useEffect, useRef } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useErrorMessage } from 'features/store/pages/pickup-store-selector/components/store-card/hooks/use-error-message';
import { Animated, StyleSheet } from 'react-native';

import ActionButton from 'components/action-button';
import { Box } from 'components/ui';
import { LogUserErrorSeverity, useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { t } from 'state/intl/translations';
import { useOrderContext } from 'state/order';
import { getDesignToken, spacing } from 'styles/constants';
import { isNative } from 'utils/environment';

import { StoreOrderButtonProps } from './types';

export const StoreOrderButton = ({
  restaurant,
  onOrderPress,
  isInSupportForm,
  disabled,
  orderHereSource,
}: StoreOrderButtonProps) => {
  const {
    fetchingPosData,
    reorder: { reordering },
  } = useOrderContext();

  const message = useErrorMessage({ restaurant });

  useLogUserErrorMessage({
    message,
    severity: LogUserErrorSeverity.Irrecoverable,
    context: {
      moreInfo: 'store-actions.tsx',
      storeId: restaurant?.storeId,
    },
  });

  /**
   * FADE-IN ReactNative ANIMATION
   */
  const fadeAnim = useRef(new Animated.Value(0));
  useEffect(() => {
    Animated.timing(fadeAnim.current, {
      toValue: 1,
      duration: 200,
      useNativeDriver: isNative,
    }).start();
  }, []);

  const buttonTextId = isInSupportForm ? 'select' : 'orderHere';

  // Loading state covers two scenarios:
  // - fetchingPosData: Loading store POS connectivity/menu data for regular ordering
  // - reordering: Processing a reorder request (users may be redirected here to select a store if no store selected)
  const isLoading = fetchingPosData || reordering;

  return (
    <Animated.View style={[{ opacity: fadeAnim.current }]}>
      <Box style={styles.ctaWrapper}>
        <ActionButton
          isLoading={isLoading}
          testID="store-modal-order-here"
          fullWidth
          onPress={onOrderPress}
          eventAttributes={orderHereSource ? { Source: orderHereSource } : undefined}
          disabled={disabled}
          leftIcon={
            <Box style={styles.iconWrapper}>
              <Icon
                size="$5"
                color={getDesignToken('icon-button-primary')}
                variant="location"
                aria-hidden
              />
            </Box>
          }
        >
          {t(buttonTextId)}
        </ActionButton>
      </Box>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  ctaWrapper: {
    paddingTop: spacing.$6,
  },
  iconWrapper: {
    justifyContent: 'center',
  },
});
